import * as React from 'react'

import { formatLocationSubtitle } from '~/client/src/shared/components/HierarchyChains'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import { useTagsStore } from './useTagsStore'

const LBS_SEPARATOR = '/'

/**
 * A location record's LBS, top-down - "Site/Building 3/Level 1" - or null when it has no
 * parent.
 *
 * Lives in a hook rather than in the resource definition because nothing below the view can
 * walk a hierarchy: ResourceValueResolver takes only the record, and IResourceRuntimeContext
 * carries no stores. The chain is read off tagsStore, the same walk the desktop table's
 * location cells do.
 */
export function useLocationLbs(
  separator: string = LBS_SEPARATOR,
): (record: IResourceRecord) => string | null {
  const tagsStore = useTagsStore()

  return React.useCallback(
    (record: IResourceRecord) => {
      const parentId = (record?.parent as any)?.parentId
      const parent = parentId
        ? (tagsStore.getTagById(String(parentId)) as LocationBase | undefined)
        : null
      if (!parent) return null

      // getHierarchyChains walks UP from the location it is called on and collects only
      // ancestors, NEAREST-FIRST - the location itself is never in the result. Reversed here
      // so the chain reads top-down, "Site/Building 3/Level 1", the way a breadcrumb does.
      // The parent's own name is appended rather than prepended for the same reason, and it
      // has to be there at all: without it a location directly under the project root would
      // show nothing, the root having no ancestors of its own.
      return (
        [
          ...parent
            .getHierarchyChains(tagsStore.tagStoreByTagTypeMap)
            .slice()
            .reverse(),
          parent.name,
        ]
          .filter(Boolean)
          .join(separator) || null
      )
    },
    [tagsStore, separator],
  )
}

/**
 * Appends a location's LBS to its subtitle, giving "LOC-023 · Site/Building 3/Level 1".
 * With no subtitle the location's own ID leads instead.
 *
 * Returns the subtitle untouched for any other resource type, so a shared surface can call
 * it unconditionally.
 */
export function useLocationSubtitle(
  resourceTypeId?: string,
): (record: IResourceRecord, subtitle: string | null) => string | null {
  const getLbs = useLocationLbs()

  return React.useCallback(
    (record: IResourceRecord, subtitle: string | null) => {
      if (resourceTypeId !== ResourceTypeId.LOCATIONS) return subtitle

      return formatLocationSubtitle(
        subtitle ?? record.stxResourceId,
        getLbs(record),
      )
    },
    [getLbs, resourceTypeId],
  )
}
